import { X, Trash2, ShoppingBag, Plus, Minus } from "lucide-react";
import { useCartStore, useUIStore, useAuthStore } from "@/lib/stores";
import { formatINR } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";

export default function CartDrawer() {
    const { cartOpen, setCartOpen, setLoginOpen } = useUIStore();
    const { items, updateQty, removeItem, totalListPrice, totalPayable, totalSavings } = useCartStore();
    const { user } = useAuthStore();
    const nav = useNavigate();

    const onCheckout = () => {
        if (!user) {
            setCartOpen(false);
            setLoginOpen(true);
            return;
        }
        setCartOpen(false);
        nav("/checkout");
    };

    if (!cartOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex justify-end bg-black/55"
            onClick={() => setCartOpen(false)}
            data-testid="cart-drawer"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full sm:max-w-md bg-white h-full flex flex-col shadow-2xl rk-fade-up"
            >
                <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E0D5]">
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-[#6C6F7A]">Your cart</div>
                        <h3 className="font-serif text-2xl font-semibold">{items.length} item{items.length !== 1 ? "s" : ""}</h3>
                    </div>
                    <button onClick={() => setCartOpen(false)} data-testid="close-cart-btn" className="p-2 rounded-full hover:bg-[#EFECE2]">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {items.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center">
                            <ShoppingBag className="w-12 h-12 text-[#E5E0D5] mb-3" />
                            <p className="font-serif text-xl text-[#0B1437]">Your cart is empty</p>
                            <p className="text-sm text-[#6C6F7A] mt-1">Add gift cards to get started</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {items.map((it) => (
                                <div
                                    data-testid={`cart-item-${it.key}`}
                                    key={it.key}
                                    className="flex gap-3 border border-[#E5E0D5] rounded-xl p-3"
                                >
                                    <div className="w-16 h-16 rk-logo-box flex items-center justify-center shrink-0">
                                        <BrandLogo brand={it} size="sm" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <p className="text-[10px] uppercase tracking-wider text-[#6C6F7A]">{it.category}</p>
                                                <p className="font-semibold text-sm truncate">{it.brand_name}</p>
                                                <p className="text-xs text-[#6C6F7A]">Value: {formatINR(it.denomination)}</p>
                                            </div>
                                            <button onClick={() => removeItem(it.key)} data-testid={`remove-${it.key}`} className="text-[#6C6F7A] hover:text-[#0B1437]">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="mt-2 flex items-center justify-between">
                                            <div className="inline-flex items-center border border-[#E5E0D5] rounded-full">
                                                <button onClick={() => updateQty(it.key, it.quantity - 1)} data-testid={`minus-${it.key}`} className="p-1.5 hover:text-[#0B1437]"><Minus className="w-3 h-3" /></button>
                                                <span className="px-2 text-sm font-semibold">{it.quantity}</span>
                                                <button onClick={() => updateQty(it.key, it.quantity + 1)} data-testid={`plus-${it.key}`} className="p-1.5 hover:text-[#0B1437]"><Plus className="w-3 h-3" /></button>
                                            </div>
                                            <div className="text-sm">
                                                {it.discount_percentage > 0 && (
                                                    <span className="text-[10px] text-[#6C6F7A] line-through mr-1">
                                                        {formatINR(it.denomination * it.quantity)}
                                                    </span>
                                                )}
                                                <span className="font-semibold text-[#0B1437]">
                                                    {formatINR(it.denomination * it.quantity * (1 - (it.discount_percentage || 0) / 100))}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {items.length > 0 && (
                    <div className="border-t border-[#E5E0D5] px-6 py-5 bg-[#F7F4EC]">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-[#6C6F7A]">Total value</span>
                            <span className="line-through text-[#6C6F7A]">{formatINR(totalListPrice())}</span>
                        </div>
                        {totalSavings() > 0 && (
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-[#1D5C42]">You save</span>
                                <span className="text-[#1D5C42] font-semibold">- {formatINR(totalSavings())}</span>
                            </div>
                        )}
                        <div className="flex items-center justify-between mt-2 text-lg font-serif">
                            <span className="font-semibold">You pay</span>
                            <span data-testid="cart-total" className="font-bold text-[#0B1437] text-2xl">{formatINR(totalPayable())}</span>
                        </div>
                        <button
                            data-testid="checkout-btn"
                            onClick={onCheckout}
                            className="rk-btn-primary w-full mt-4 !py-3.5 !text-base"
                        >
                            Proceed to Checkout
                        </button>
                        <p className="text-[10px] text-center text-[#6C6F7A] uppercase tracking-wider mt-3">
                            Secure payments · Instant delivery
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
